import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Loader2, FileText, Download } from 'lucide-react';
import { receiptsApi } from '../api';
import { fmtBytes } from '../expenseConstants';

// Authenticated thumbnail — fetches the file as a blob (no public URLs).
export const ReceiptThumb = ({ receipt, className = '' }) => {
  const [url, setUrl] = useState(null);
  const isImage = receipt.mime_type?.startsWith('image/');

  useEffect(() => {
    let objectUrl;
    if (isImage) {
      receiptsApi
        .fileBlob(receipt.id)
        .then((r) => {
          objectUrl = URL.createObjectURL(r.data);
          setUrl(objectUrl);
        })
        .catch(() => {});
    }
    return () => objectUrl && URL.revokeObjectURL(objectUrl);
  }, [receipt.id, isImage]);

  if (!isImage) {
    return (
      <div className={`flex items-center justify-center bg-[#1F1F1F] text-gray-500 ${className}`}>
        <FileText size={24} className="text-[#D4AF37]" />
      </div>
    );
  }
  return url ? (
    <img src={url} alt={receipt.original_filename} className={`object-cover ${className}`} />
  ) : (
    <div className={`flex items-center justify-center bg-[#1F1F1F] ${className}`}>
      <Loader2 size={16} className="animate-spin text-gray-600" />
    </div>
  );
};

export const downloadReceipt = async (receipt) => {
  const r = await receiptsApi.fileBlob(receipt.id);
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = receipt.original_filename || 'receipt';
  a.click();
  URL.revokeObjectURL(url);
};

export const ReceiptPreviewDialog = ({ open, onOpenChange, receipt }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let objectUrl;
    if (open && receipt) {
      setLoading(true);
      receiptsApi
        .fileBlob(receipt.id)
        .then((r) => {
          objectUrl = URL.createObjectURL(r.data);
          setUrl(objectUrl);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setUrl(null);
    }
    return () => objectUrl && URL.revokeObjectURL(objectUrl);
  }, [open, receipt]);

  if (!receipt) return null;
  const isImage = receipt.mime_type?.startsWith('image/');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="receipt-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base break-all">{receipt.original_filename}</DialogTitle>
          <DialogDescription className="text-gray-500">
            {fmtBytes(receipt.size_bytes)} · {receipt.mime_type}
            {receipt.project ? ` · ${receipt.project.name}` : ''}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-16 text-gray-500">
            <Loader2 size={26} className="mx-auto mb-2 animate-spin text-[#D4AF37]" />
            Loading receipt…
          </div>
        ) : isImage && url ? (
          <img src={url} alt={receipt.original_filename} className="max-h-[65vh] w-auto mx-auto rounded-md" />
        ) : url ? (
          <iframe src={url} title="Receipt PDF" className="w-full h-[65vh] rounded-md bg-white" />
        ) : (
          <p className="text-gray-500 text-sm py-8 text-center">Preview unavailable. Use download instead.</p>
        )}

        <div className="flex justify-end pt-2">
          <Button
            onClick={() => downloadReceipt(receipt)}
            variant="outline"
            className="border-gray-700 text-gray-300"
            data-testid="receipt-download-btn"
          >
            <Download size={15} /> Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
